import { useCallback, useContext } from "react";

import { getCurrentChainConfig } from "../../config/chains";
import { ErrorDialogContext } from "../../contexts/ErrorDialogContext";
import { isWrongNetworkError } from "../../utils/wallet/activeSigner";
import { useWallet } from "./useWallet";

/**
 * Turn a `WrongNetworkError` into a dialog the user can act on.
 *
 * `getSignerForChain` refuses to sign when the wallet is on another network, but
 * refusing is only half a fix: a trader who is told "wrong network" still has to
 * work out which network, and go and change it. This offers the switch instead,
 * reusing `useWallet().setChainToSwitch` — the same path the connect modal uses,
 * which already handles SDK adapters and the EIP-3085 add-chain case.
 *
 * Returns `true` when it handled the error, so a call site keeps its existing
 * reporting for everything else:
 *
 *     catch (e) {
 *       if (!handleWrongNetwork(e)) addErrorToast(<p>Order Failed</p>, e.message);
 *     }
 *
 * One hook rather than a copy of this logic at each catch site — the wrong-chain
 * condition is identical wherever it surfaces, and only the fallback differs.
 */
export const useWrongNetworkHandler = () => {
  const { showError } = useContext(ErrorDialogContext);
  const { setChainToSwitch } = useWallet();

  return useCallback(
    (error: unknown): boolean => {
      if (!isWrongNetworkError(error)) return false;

      const config = getCurrentChainConfig();
      showError("Wrong network", error.message, {
        label: `Switch to ${config.name}`,
        onClick: () => {
          // Fire and forget: the wallet owns the prompt from here, and a
          // rejection is the user's answer, not an error to re-report.
          void setChainToSwitch(config.chainId).catch(() => {});
        },
      });
      return true;
    },
    [showError, setChainToSwitch]
  );
};
